import { Button } from '@heroui/react';
import { format, parseISO } from 'date-fns';
import type { IStudentScormPackage } from '@/entities/Scorm';

/** preview — учитель, только название и кнопка; review — учитель смотрит сессию; launch — студент; player — шапка в модалке */
type ScormCardMode = 'preview' | 'review' | 'launch' | 'player';

interface ScormCardProps {
  scorm: IStudentScormPackage;
  mode?: ScormCardMode;
  onAction: () => void;
  isLoading?: boolean;
  /** Переопределяет текст кнопки */
  actionLabel?: string;
}

const progressPercent = (value: number | null | undefined): number =>
  value !== null && value !== undefined ? Math.round(value * 100) : 0;

const progressLabel = (session: IStudentScormPackage['session']): string => {
  if (!session) return 'Not Started';
  if (session.completionStatus === 'completed') return 'Completed';
  return 'In progress';
};

/** Статус прохождения оценки: Passed | Failed, только если есть assessment и порог */
const getAssessmentResultLabel = (scorm: IStudentScormPackage): string | null => {
  const { session, hasAssessment, passingScore } = scorm;
  if (!hasAssessment || passingScore === null || passingScore === undefined) return null;
  const scoreScaled = session?.scoreScaled;
  if (scoreScaled === null || scoreScaled === undefined) return null;
  return scoreScaled * 100 >= passingScore ? 'Passed' : 'Failed';
};

const hasSession = (session: IStudentScormPackage['session']): boolean =>
  !!session && session.completionStatus !== 'completed';

const buttonLabel = (mode: ScormCardMode, session: IStudentScormPackage['session']): string => {
  if (mode === 'player') return 'Exit';
  if (mode === 'preview' || mode === 'review') return 'Open Lesson';
  if (session?.completionStatus === 'completed') return 'Open Lesson';
  return session ? 'Keep Going' : 'Start';
};

const formatLastActivity = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'dd.MM.yyyy, HH:mm');
  } catch {
    return iso;
  }
};

/** Форматирует секунды в "Xh Ym" или "Xm" или "Xs" */
const formatTotalTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return min > 0 ? `${h}h ${min}m` : `${h}h`;
};

export const ScormCard = ({
  scorm,
  mode = 'preview',
  onAction,
  isLoading,
  actionLabel,
}: ScormCardProps) => {
  const progress = scorm.session?.completionStatus === 'completed' ? progressPercent(1) : progressPercent(scorm.session?.progressMeasure);
  const score = scorm.session?.scoreScaled;
  const totalTime = scorm.session?.totalTime ?? 0;
  const lastActivityAt = scorm.session?.lastActivityAt ?? null;
  const inProgress = hasSession(scorm.session);
  const progressStatus = progressLabel(scorm.session);
  const assessmentResult = getAssessmentResultLabel(scorm);
  const showProgressBlock = mode !== 'preview';
  const showTimeAndActivity = mode === 'review';
  const showAutoSaveText = mode === 'launch' || mode === 'player';

  return (
    <div className="rounded-2xl bg-red p-6 text-white flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {showProgressBlock ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium opacity-90">Progress {progress}%</span>
                <div className="h-1.5 w-[180px] rounded-full bg-white/30 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-green-secondary transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-sm font-medium ${inProgress ? 'bg-blue text-white' : 'bg-grey-100 text-black-headline'}`}
              >
                {progressStatus}
              </span>
              {score !== null && score !== undefined && (
                <span className="text-lg font-medium">Score: {Math.round(score * 100)}%</span>
              )}
              {assessmentResult !== null && (
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium ${assessmentResult === 'Passed' ? 'bg-green-positive text-green-tertiary' : 'bg-red-secondary text-red-tertiary'}`}
                >
                  {assessmentResult}
                </span>
              )}

              {showTimeAndActivity && totalTime > 0 && (
                <span className="text-sm font-medium opacity-90">
                  Time Spent: {formatTotalTime(totalTime)}
                </span>
              )}
              {showTimeAndActivity && lastActivityAt && (
                <span className="text-sm font-medium opacity-90">
                  Last Activity: {formatLastActivity(lastActivityAt)}
                </span>
              )}

            </>
          ) : <h3 className="text-xl font-medium leading-tight">{scorm.title}</h3>}
        </div>

        <Button
          color="default"
          className="bg-white text-black-headline font-medium text-[16px] min-w-[120px]"
          radius="full"
          onPress={onAction}
          isLoading={isLoading}
        >
          {actionLabel ?? buttonLabel(mode, scorm.session)}
        </Button>
      </div>

      {showProgressBlock && <h3 className="text-xl font-medium leading-tight">{scorm.title}</h3>}

      {showAutoSaveText && (
        <p className="text-sm opacity-90">Your progress will be saved automatically</p>
      )}
    </div>
  );
};
